import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useTranslation } from "react-i18next";

const TermsAndConditions = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <div className="from-top">
        <Header />

        <div className="heading-policy flex-column text-center">
          <h1>Terms & Conditions</h1>
        </div>
      </div>

      <div className="container">
        <div className="text-center">
          <p>
            Website: <a href="https://bazarnauae.com">https://bazarnauae.com</a>
          </p>
        </div>
        <div className="privacy-heading">
          <h3>1. Introduction</h3>
          <p>
            Welcome to Bazarna (the "App"), a community marketplace for the United Arab Emirates provided by <strong>Bazarna</strong>. Bazarna lets you buy, sell, or gift items, discover local home businesses, and find or add community food fridges. By downloading, installing, accessing or using the App (and/or website at <a href="https://bazarnauae.com">https://bazarnauae.com</a>) (the "Service"), you ("you", "User", "Member") agree to be bound by these terms and conditions ("Terms"). If you do not agree, do not use the Service.
          </p>

          <h3 className="mt-5">2. Definitions</h3>
          <ul>
            <li><strong>"App" or "Service"</strong>: the mobile application and/or website platform provided by Bazarna including any updates, features, functionalities.</li>
            <li><strong>"Buyer"</strong>: a User who browses, orders, or receives a gifted or donated item through the Service.</li>
            <li><strong>"Seller"</strong>: a User who creates a store on Bazarna to list items for sale or as free gifts.</li>
            <li><strong>"Listing"</strong>: any item, priced or gifted, that a Seller posts on the Service.</li>
            <li><strong>"Food Fridge"</strong>: a community food-sharing location added by a User and shown on the Service's map for donating or collecting food items.</li>
            <li><strong>"User Content"</strong>: any content which you upload, post or otherwise transmit through the Service, including listings, photos, reviews, and Food Fridge submissions.</li>
            <li><strong>"We", "us", "our"</strong>: Bazarna and its affiliates.</li>
            <li><strong>"Account"</strong>: the user or seller account you create to use the Service.</li>
          </ul>

          <h3 className="mt-5">3. Eligibility</h3>
          <p>
            You must be at least 18 years old (or such minimum age as per applicable local law) in order to use the Service. If you are registering as a Seller on behalf of a company or entity, you represent that you are authorised to do so and that any trade licence or business information you provide is accurate.
          </p>

          <h3 className="mt-5">4. Account Registration & Security</h3>
          <ul>
            <li>You must register an Account to access certain features. You agree to provide accurate, current and complete information and to update it as required.</li>
            <li>Seller accounts are subject to our approval and may be rejected or suspended if the information or documentation provided (e.g. trade licence) is incomplete, inaccurate, or does not meet our onboarding requirements.</li>
            <li>You are responsible for safeguarding your login credentials and for all activities that occur under your Account. You must notify us immediately in case of any unauthorised use.</li>
            <li>We reserve the right to suspend or terminate Accounts at our discretion (e.g., for breaches of these Terms).</li>
          </ul>

          <h3 className="mt-5">5. Bazarna's Role as a Marketplace</h3>
          <p>
            Bazarna is a platform that connects Buyers and Sellers within the community; we are not a party to the sale, gift, or exchange agreed between Users, and we do not manufacture, own, inspect, or take title to any Listing. Sellers are solely responsible for the accuracy of their Listings and for fulfilling their obligations to Buyers. We encourage Users to communicate clearly, inspect items on delivery, and use the Service's rating and review features responsibly.
          </p>

          <h3 className="mt-5">6. Buying on Bazarna</h3>
          <ul>
            <li>Buyers may pay for orders by card, Apple Pay, or cash on delivery, where available. Promo codes and discounts are subject to the terms displayed at the time of use and may be withdrawn or amended at any time.</li>
            <li>Order status, dispatch, and delivery are tracked within the App. Delivery timeframes provided by Sellers or delivery partners are estimates and not guaranteed.</li>
            <li>Cancellations, returns, and refunds are handled in line with the policy communicated at checkout or by the Seller; contact us if you are unable to resolve an issue directly with a Seller.</li>
          </ul>

          <h3 className="mt-5">7. Selling on Bazarna</h3>
          <ul>
            <li>Sellers may create a storefront, list items for sale, and manage orders through their seller dashboard. Commercial sellers may be required to provide a valid trade licence.</li>
            <li>Sellers are responsible for listing items accurately, fulfilling accepted orders, and complying with all applicable UAE laws, including consumer protection and product safety requirements.</li>
            <li>Payouts to Sellers are processed to the Seller's in-app wallet in accordance with our applicable fees and payout schedule, which may be updated from time to time.</li>
            <li>We may reject, suspend, or remove any Listing or Seller Account that violates these Terms or applicable law.</li>
          </ul>

          <h3 className="mt-5">8. Gifting & Community Food Fridges</h3>
          <ul>
            <li>Users may list items as free gifts, and may add or use community Food Fridges to donate or collect food items.</li>
            <li>Bazarna does not inspect, verify, or guarantee the safety, freshness, or quality of any gifted item or food placed at a Food Fridge. Users donate and collect items at their own risk and are responsible for exercising reasonable care (e.g. checking expiry dates and storage conditions).</li>
            <li>Food Fridge locations are submitted by Users; we do not guarantee their accuracy or availability at any given time.</li>
          </ul>

          <h3 className="mt-5">9. Prohibited Items & Conduct</h3>
          <p>You agree not to:</p>
          <ul>
            <li>list or exchange items that are illegal, counterfeit, stolen, hazardous, or prohibited under UAE law;</li>
            <li>use the Service for any illegal purpose or in violation of UAE law or other applicable law;</li>
            <li>upload, post or transmit any User Content that is defamatory, obscene, infringing, or otherwise objectionable;</li>
            <li>post fake reviews or ratings, or otherwise manipulate the Service's trust and safety features;</li>
            <li>interfere with or disrupt the Service or servers or networks connected to the Service;</li>
            <li>attempt to gain unauthorised access to other accounts, computer systems or networks;</li>
            <li>use the Service in any manner that could disable, overburden, or impair the Service.</li>
          </ul>

          <h3 className="mt-5">10. License to Use the Service</h3>
          <p>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence to download, install and use the App on a device that you own or control. You agree not to copy, modify, distribute, sell, reverse engineer or otherwise misuse the Service.
          </p>

          <h3 className="mt-5">11. User Content</h3>
          <ul>
            <li>You retain ownership of your User Content. By uploading or posting User Content (including Listing photos and descriptions, reviews, and Food Fridge submissions), you grant us a worldwide, non-exclusive, royalty-free, sublicensable licence to host, store, use, reproduce, modify, create derivative works, communicate, publish, perform or display such User Content in order to provide the Service.</li>
            <li>We may remove or disable access to any User Content at our discretion, including if it violates these Terms.</li>
          </ul>

          <h3 className="mt-5">12. Intellectual Property Rights</h3>
          <p>
            All rights, title and interest in and to the Service (excluding your User Content) are and shall remain with us and/or our licensors. The Service is protected by copyright, trademark, patent or other proprietary rights. You agree not to copy, reproduce, distribute, publicly display, translate or create derivative works of the Service.
          </p>

          <h3 className="mt-5">13. Disclaimers & Warranties</h3>
          <ul>
            <li>The Service is provided "as is" and "as available", without warranties of any kind, whether express or implied.</li>
            <li>We do not warrant that the Service will be uninterrupted, secure, error-free or free of harmful components.</li>
            <li>You acknowledge that you use the Service, including any transaction, gift, or Food Fridge exchange arranged through it, at your own risk.</li>
          </ul>

          <h3 className="mt-5">14. Indemnification</h3>
          <p>
            You agree to indemnify, defend and hold us harmless from and against all claims, damages, liabilities, costs and expenses (including reasonable legal fees) arising out of or related to your use of the Service, your Listings or User Content, your breach of these Terms, or your violation of applicable laws.
          </p>

          <h3 className="mt-5">15. Governing Law & Dispute Resolution</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates and the Emirate of Dubai, without regard to conflict of law provisions. Any dispute arising out of or relating to these Terms or the Service shall be submitted exclusively to the courts of Dubai.
          </p>

          <h3 className="mt-5">16. Changes to Terms</h3>
          <p>
            We may modify these Terms at any time. We will notify you by posting the revised Terms on the App/website and/or by email. Your continued use of the Service after such changes constitutes your acceptance of the new Terms. It's recommended you review the Terms periodically.
          </p>

          <h3 className="mt-5">17. Termination</h3>
          <p>
            We may suspend or terminate your access to the Service or delete your Account at our sole discretion, without notice, if you breach these Terms or for any reason. Upon termination, your right to use the Service ceases immediately. The provisions which by their nature should survive termination shall survive.
          </p>

          <h3 className="mt-5">18. Miscellaneous</h3>
          <ul>
            <li>If any provision of these Terms is held invalid or unenforceable, the remaining provisions will remain in full force and effect.</li>
            <li>No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.</li>
            <li>These Terms constitute the entire agreement between you and us regarding your use of the Service and supersede all prior or contemporaneous understandings or agreements regarding same.</li>
          </ul>

          <h3 className="mt-5">19. Contact Information</h3>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p>
            <strong>Bazarna FZ-LLC</strong>
          </p>
          <p>Business Bay, Dubai, United Arab Emirates</p>
          <p>
            Email: <strong>info@bazarnauae.com</strong>
          </p>
          <p>
            Website: <a href="https://bazarnauae.com">https://bazarnauae.com</a>
          </p>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TermsAndConditions;
